import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./add-contract-work.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import { useAddContractWorkMutation } from "../contractWorksApi";
import { useGetAllWorksQuery } from "../../workTypes/workTypesApi";
import { HHMM2min } from "../../../../../helpers/time";
import { useGetAllContractAircraftsQuery } from "../../contractAircrafts/contractAircraftApi";

type Props = {
  contract_id: number;
  close: () => void;
};

type State = {
  errors?: {
    id?: string[];
    work_id?: string[];
    aircraft_type_id?: string[];
    limit_time?: string[];
    cost?: string[];
  };
  message?: string | null;
};

type TransportOptionType = {
  id: number;
  name: string;
};

const FormSchema = z.object({
  work_id: z.coerce.number({
    message: "Выберите вид работ из списка"
  }),
  aircraft_type_id: z.coerce.number({
    message: "Выберите тип ВС из списка"
  }),
  limit_time: z.string().transform((val) => {
    return HHMM2min(val);
  }),
  cost: z.coerce.number(),
});

const Form: FC<Props> = ({ contract_id, close }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [transportOptions, setTransportOptions] = useState<
    TransportOptionType[] | []
  >([]);
  const [add, { isError, isLoading, isSuccess }] = useAddContractWorkMutation();
  const {
    data: works,
    isFetching: isWorkFetching,
    isError: isWorkError,
  } = useGetAllWorksQuery();
  const {
    data: transport,
    isFetching: isTransportFetching,
    isError: isTransportError,
  } = useGetAllContractAircraftsQuery(contract_id);

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      work_id: formData.get("work_id"),
      aircraft_type_id: formData.get("aircraft_type_id"),
      limit_time: formData.get("limit_time"),
      cost: formData.get("cost"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const { work_id, aircraft_type_id, limit_time, cost } =
      validatedFields.data;

    add({
      contract_id,
      work_id,
      aircraft_type_id,
      limit_time,
      cost,
    });
  };

  useEffect(() => {
    if (!transport) return;
    const keys = [] as number[];
    const options = [] as TransportOptionType[];

    transport.forEach((unit) => {
      if(!unit.transport) return;
      if (keys.includes(unit.transport.avia_model_id)) return;
      options.push({
        id: unit.transport.avia_model_id,
        name: unit.transport.name,
      });
      keys.push(unit.transport.avia_model_id)
    });

    setTransportOptions(options);
  }, [transport]);

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  useEffect(() => {
    console.log(transportOptions);
  }, [transportOptions]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Добавление работ по контракту</div>
      <div className={styles.row}>
        <label htmlFor="aircraft_type_id">Тип ВС</label>
        <select className={styles.select} name="aircraft_type_id">
          {isTransportFetching ? (
            <option>Загрузка списка...</option>
          ) : (
            <option className={styles.option}>Выберите</option>
          )}

          {transportOptions?.map((item) => (
            <option key={item?.id} className={styles.option} value={item?.id}>
              {item?.name}
            </option>
          ))}
        </select>
        <div id="aircraft_type_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.aircraft_type_id &&
            state.errors.aircraft_type_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="work_id">Вид работ</label>
        <select className={styles.select} name="work_id">
          {isWorkFetching ? (
            <option>Загрузка списка...</option>
          ) : (
            <option className={styles.option}>Выберите</option>
          )}

          {works?.map((item) => (
            <option
              key={item.id}
              className={styles.option}
              value={item.id}
            >
              {item.name}
            </option>
          ))}
        </select>
        <div id="work_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.work_id &&
            state.errors.work_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="limit_time">Лимит летного времени, часы:минуты</label>
        <input
          className={styles.input}
          name="limit_time"
          placeholder="Лимит летного времени, часы:минуты"
        />
        <div id="limit_time-error" aria-live="polite" aria-atomic="true">
          {state.errors?.limit_time &&
            state.errors.limit_time.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="cost">Стоимость 1 часа, руб.</label>
        <input
          className={styles.input}
          name="cost"
          placeholder="Стоимость 1 часа, руб."
        />
        <div id="cost-error" aria-live="polite" aria-atomic="true">
          {state.errors?.cost &&
            state.errors.cost.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isWorkError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
          autoHideDuration={3000}
        >
          Не удалось загрузить список видов работ.
        </Snackbar>
      )}
      {isTransportError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
          autoHideDuration={3000}
        >
          Не удалось загрузить список типов ВС.
        </Snackbar>
      )}
    </form>
  );
};

export const AddContractWork: FC<Props> = ({ close, contract_id }) => {
  return (
    <Modal close={close}>
      <Form close={close} contract_id={contract_id} />
    </Modal>
  );
};
